import { track } from '../../services/analytics';

// How many pan/zoom gestures separate a visitor who actually worked the map from
// one who nudged it once and left. Deliberately low: the question is "did they
// explore at all", not "how hard".
const EXPLORE_GESTURE_THRESHOLD = 5;

// Reports how the visitor moves the map surface. Three events, each sent at most
// once per planner boot: planner_map_pan on the first drag, planner_map_zoom on
// the first zoom they caused, planner_map_explore once the two together reach
// EXPLORE_GESTURE_THRESHOLD. Capping each at one keeps a pinch-happy session from
// flooding GA4 while still answering whether the map got used.
//
// Zoom reporting is gated on suppress(): Google fires zoom_changed for
// programmatic moves too, and the planner makes several (the opening fit on
// Serbia, the route fit, the cluster/group tap zoom), so counting zoom_changed
// alone would largely measure our own animations. dragend needs no such guard -
// panTo and fitBounds do not fire it, only a real drag does.
export class MapGestureTracker {
  private readonly listeners: google.maps.MapsEventListener[] = [];
  private suppressed = 0;
  private lastZoom: number;
  private pendingZoom: 'in' | 'out' | null = null;
  private pannedReported = false;
  private zoomedReported = false;
  private exploreReported = false;
  private gestures = 0;

  constructor(private readonly map: google.maps.Map) {
    this.lastZoom = map.getZoom() ?? 0;

    this.listeners.push(
      map.addListener('dragend', () => {
        if (!this.pannedReported) {
          this.pannedReported = true;
          track('planner_map_pan');
        }
        this.countGesture();
      })
    );

    // A pinch emits zoom_changed continuously, so this only records the pending
    // direction; 'idle' below closes the whole gesture out as one.
    this.listeners.push(
      map.addListener('zoom_changed', () => {
        const zoom = this.map.getZoom() ?? this.lastZoom;
        const previous = this.lastZoom;
        this.lastZoom = zoom;
        if (this.suppressed > 0 || zoom === previous) return;
        this.pendingZoom = zoom > previous ? 'in' : 'out';
      })
    );

    this.listeners.push(
      map.addListener('idle', () => {
        const direction = this.pendingZoom;
        if (!direction) return;
        this.pendingZoom = null;
        if (!this.zoomedReported) {
          this.zoomedReported = true;
          track('planner_map_zoom', { direction });
        }
        this.countGesture();
      })
    );
  }

  // Marks a move the planner makes itself, so it is not read as the visitor's.
  // A counter rather than a flag: fits can overlap (a route recompute landing
  // while a cluster tap is still settling) and the first idle must not unmask
  // the second.
  suppress(): void {
    this.suppressed++;
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      this.suppressed--;
    });
  }

  destroy(): void {
    for (const l of this.listeners) l.remove();
    this.listeners.length = 0;
  }

  private countGesture(): void {
    if (this.exploreReported) return;
    this.gestures++;
    if (this.gestures < EXPLORE_GESTURE_THRESHOLD) return;
    this.exploreReported = true;
    track('planner_map_explore');
  }
}
